import type {
  PromotionApplyMode,
  PromotionKind,
  PromotionScope,
  PromotionTargetType,
  PromotionWithTargets,
} from '../domain/promotionTypes';
import { formatMoney } from '../../shared/format/public-api';

export const PROMOTION_KIND_LABELS: Record<PromotionKind, string> = {
  percent_off: '% de descuento',
  fixed_off: 'Monto fijo ($)',
  combo_buy_x_get_y: '2×1 / 3×2',
  combo_n_for_price: 'N por un precio',
  manual: 'A mano en caja',
};

export const PROMOTION_SCOPE_LABELS: Record<PromotionScope, string> = {
  line: 'Por producto',
  category: 'Por categoría',
  cart: 'Carrito completo',
};

export const PROMOTION_APPLY_MODE_LABELS: Record<PromotionApplyMode, string> = {
  automatic: 'Automática',
  manual_code: 'Código promo',
  cashier_select: 'La elige el cajero',
};

export const PROMOTION_APPLY_MODE_HELPERS: Record<PromotionApplyMode, string> = {
  automatic: 'Se aplica sola si el carrito entra. Sirve para 2×1 o un %.',
  manual_code: 'El cajero o el cliente pone un código. Ej. VERANO20.',
  cashier_select: 'Sale en el POS. El cajero elige cuándo usarla. Solo mostrador.',
};

export const PROMOTION_KIND_HINTS: Partial<Record<PromotionKind, string>> = {
  percent_off: '15% en bebidas',
  fixed_off: '$500 menos',
  combo_buy_x_get_y: '2×1 · 3×2',
  combo_n_for_price: '3 por $2000',
};

export const PROMOTION_TARGET_TYPE_LABELS: Record<PromotionTargetType, string> = {
  product: 'Producto',
  category: 'Categoría',
  brand: 'Marca',
  variant: 'Variante',
};

export const PROMOTION_APPLIES_TO_HELPER =
  'Sin filtros vale para todo el catálogo. Agregá productos, categorías, marcas o variantes para acotar.';

/** Alcance (`scope`) = cómo se reparte el descuento; no elige categorías (eso es «A qué productos»). */
export function getPromotionScopeHelperText(scope: PromotionScope, kind?: PromotionKind): string {
  const isCombo = kind === 'combo_buy_x_get_y' || kind === 'combo_n_for_price';
  switch (scope) {
    case 'line':
      return isCombo
        ? 'El combo cuenta unidades del mismo producto en esa línea.'
        : 'El descuento va en cada producto que entra.';
    case 'cart':
      return isCombo
        ? 'El combo junta unidades de todos los productos que entran. Dos bebidas distintas cuentan juntas.'
        : 'Un solo descuento, repartido entre los productos que entran.';
    case 'category':
      return isCombo
        ? 'Igual que el carrito: mezcla unidades de los productos que entran.'
        : 'Igual que por producto. Para limitar categorías, agregalas en «A qué productos».';
    default:
      return '';
  }
}

export function formatPromotionBenefit(promo: Pick<
  PromotionWithTargets,
  'kind' | 'valuePercent' | 'valueFixed' | 'comboBuyQty' | 'comboPayQty' | 'comboBundlePrice'
>): string {
  switch (promo.kind) {
    case 'percent_off':
      return promo.valuePercent != null ? `${promo.valuePercent}%` : '—';
    case 'fixed_off':
      return promo.valueFixed != null ? `${formatMoney(promo.valueFixed)}` : '—';
    case 'combo_buy_x_get_y':
      return promo.comboBuyQty != null && promo.comboPayQty != null
        ? `${promo.comboBuyQty}×${promo.comboPayQty}`
        : '—';
    case 'combo_n_for_price':
      return promo.comboBuyQty != null && promo.comboBundlePrice != null
        ? `${promo.comboBuyQty} por ${formatMoney(promo.comboBundlePrice)}`
        : '—';
    case 'manual':
      return 'Manual';
    default:
      return '—';
  }
}

/** Preview line for the create/edit form ticket (raw draft strings). */
export function formatPromotionDraftBenefit(input: {
  kind: PromotionKind;
  valuePercent: string;
  valueFixed: string;
  comboBuyQty: string;
  comboPayQty: string;
  comboBundlePrice: string;
}): string {
  const pct = Number(input.valuePercent.replace(',', '.'));
  const fixed = Number(input.valueFixed.replace(',', '.'));
  const buy = Number(input.comboBuyQty);
  const pay = Number(input.comboPayQty);
  const bundle = Number(input.comboBundlePrice.replace(',', '.'));
  switch (input.kind) {
    case 'percent_off':
      return Number.isFinite(pct) && pct > 0 ? `${pct}%` : '—%';
    case 'fixed_off':
      return Number.isFinite(fixed) && fixed > 0 ? `${formatMoney(fixed)}` : '$—';
    case 'combo_buy_x_get_y':
      return Number.isFinite(buy) && Number.isFinite(pay) && buy > 0 && pay > 0 ? `${buy}×${pay}` : 'N×M';
    case 'combo_n_for_price':
      return Number.isFinite(buy) && buy > 0 && Number.isFinite(bundle) && bundle > 0
        ? `${buy} por ${formatMoney(bundle)}`
        : 'N por $—';
    default:
      return '—';
  }
}

export function formatPromotionSchedule(
  startsAt?: string | null,
  endsAt?: string | null
): string {
  const parts: string[] = [];
  if (startsAt) {
    parts.push(`desde ${new Date(startsAt).toLocaleDateString('es-AR')}`);
  }
  if (endsAt) {
    parts.push(`hasta ${new Date(endsAt).toLocaleDateString('es-AR')}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Sin vencimiento';
}

/** Detalle secundario para filas de listado (sin repetir beneficio ni estado). */
export function formatPromotionListDetail(promo: PromotionWithTargets): string {
  const parts: string[] = [];
  const code = promo.promoCode?.trim();
  if (code) parts.push(`Código ${code}`);

  const schedule = formatPromotionSchedule(promo.startsAt, promo.endsAt);
  if (schedule !== 'Sin vencimiento') {
    parts.push(schedule);
  }

  const targetCount = promo.targets.length;
  if (targetCount === 0) {
    parts.push('Todo el catálogo');
  } else if (targetCount === 1) {
    parts.push('1 filtro');
  } else {
    parts.push(`${targetCount} filtros`);
  }

  return parts.join(' · ');
}

export function promotionStatusLabel(promo: Pick<PromotionWithTargets, 'isActive' | 'startsAt' | 'endsAt'>): string {
  if (!promo.isActive) return 'Inactiva';
  const now = Date.now();
  if (promo.startsAt) {
    const startMs = Date.parse(promo.startsAt);
    if (!Number.isNaN(startMs) && startMs > now) return 'Programada';
  }
  if (promo.endsAt) {
    const endMs = Date.parse(promo.endsAt);
    if (!Number.isNaN(endMs) && endMs < now) return 'Vencida';
  }
  return 'Activa';
}

export type PromotionStatusTone = 'success' | 'warning' | 'neutral';

export function promotionStatusTone(
  promo: Pick<PromotionWithTargets, 'isActive' | 'startsAt' | 'endsAt'>
): PromotionStatusTone {
  switch (promotionStatusLabel(promo)) {
    case 'Activa':
      return 'success';
    case 'Vencida':
      return 'warning';
    default:
      return 'neutral';
  }
}

const DAY_MS = 86_400_000;

export function formatPromotionValidity(
  promo: Pick<PromotionWithTargets, 'isActive' | 'startsAt' | 'endsAt'>
): string | null {
  const now = Date.now();
  if (promo.startsAt) {
    const startMs = Date.parse(promo.startsAt);
    if (!Number.isNaN(startMs) && startMs > now) {
      const days = Math.round((startMs - now) / DAY_MS);
      if (days === 0) return 'Empieza hoy';
      if (days === 1) return 'Empieza mañana';
      if (days <= 30) return `Empieza en ${days} días`;
      return `Empieza ${new Date(startMs).toLocaleDateString('es-AR')}`;
    }
  }
  if (promo.endsAt) {
    const endMs = Date.parse(promo.endsAt);
    if (!Number.isNaN(endMs)) {
      if (endMs < now) {
        const days = Math.round((now - endMs) / DAY_MS);
        if (days === 0) return 'Vencida hoy';
        if (days === 1) return 'Vencida ayer';
        if (days <= 30) return `Vencida hace ${days} días`;
        return `Vencida ${new Date(endMs).toLocaleDateString('es-AR')}`;
      } else {
        const days = Math.round((endMs - now) / DAY_MS);
        if (days === 0) return 'Vence hoy';
        if (days === 1) return 'Vence mañana';
        if (days <= 30) return `Vence en ${days} días`;
        return `Vence ${new Date(endMs).toLocaleDateString('es-AR')}`;
      }
    }
  }
  return null;
}
